import { defineStore } from "pinia";
import { getAssets, getConfiguredGaugesAsGeoJSON } from '@/utils/api.js';

export const defaultState = () => ({
    groupIdList: [],
    groupMap: {},
    assetIdList: [],
    assetMap: {},
    gaugesGeoJSON: null,
    allGaugesLoaded: false,
    loadStatus: 'not started', // success | failed | not started
    activeGroupId: null,
    activeAssetId: null,
    adminAssetId: null,
    drawerOpen: false,
    createAssetDrawerOpen: false,
    chartMode: 'single',
    filters: {
        searchTerm: '',
        alertOnly: false,
        includeForecasts: true,
        includePhysicalGauge: true,
        includeVirtualGauge: true,
        activeGroupOnly: false,
        sortMethod: 'Rainfall Risk Level (high to low)'
    },
    hourResolution: 3,
    streamflowChartOptions: {
        'level-1': false,
        'level-2': false,
        'level-3': false,
        'level-4': false,
        'level-5': false,
        'level-6': false,
        showAllRiskLevels: false,
        showReturnPeriods: false,
        viewMode: 'lastSevenDayFlowTimeSeries'
    },
    logReportData: [],
    loadedHistoryRanges: {
        'Last 30 days': false,
        'Last 7 days': false,
        'Last year': false,
    },
});

export const useAssetStore = defineStore('assets', {
    state: defaultState,
    getters: {
        allGroups(state) {
            return state.groupIdList.map(id => state.groupMap[id]);
        },
        allAssets(state) {
            return state.assetIdList.map(id => state.assetMap[id]);
        },
        activeGroup(state) {
            return state.groupMap[state.activeGroupId];
        },
        activeAsset(state) {
            return state.assetMap[state.activeAssetId];
        },
        filteredAssetIds() {
            const filteredIds = [];
            this.allAssets.forEach(asset => {
                // Filter for assets that include the search term in their name
                if (this.filters.searchTerm === '' || asset.name.toLowerCase().includes(this.filters.searchTerm.toLowerCase())) {
                    // Filter for assets based on what type of forecasting/gauge they have
                    if (
                        (this.filters.includeForecasts && asset.hasForecasts) ||
                        (this.filters.includePhysicalGauge && asset.hasGauge && !asset.gaugeData.isVirtual) ||
                        (this.filters.includeVirtualGauge && asset.hasGauge && asset.gaugeData.isVirtual)
                    ) {
                        // Filter for assets based on alert status
                        if (!this.filters.alertOnly || asset.forecastData.isAlerted || asset.gaugeData.isAlerted) {
                            filteredIds.push(asset.id);
                        }
                    }
                }
            });
            return filteredIds;
        },
        filteredMapAssetIds() {
            const filteredIds = [];
            this.assetIdList.forEach(id => {
                const asset = this.assetMap[id];
                // Filter for assets based on what type of forecasting/gauge they have
                if (
                    (this.filters.includeForecasts && asset.hasForecasts) ||
                    (this.filters.includePhysicalGauge && asset.hasGauge && !asset.gaugeData.isVirtual) ||
                    (this.filters.includeVirtualGauge && asset.hasGauge && asset.gaugeData.isVirtual)
                ) {
                    // Filter for assets based on alert status
                    if (!this.filters.alertOnly || asset.forecastData.isAlerted || asset.gaugeData.isAlerted) {
                        // Filter based on active group
                        if (!this.filters.activeGroupOnly || asset.groupIds.includes(this.activeGroupId)) {
                            filteredIds.push(asset.id);
                        }
                    }
                }
            });
            return filteredIds;
        },
    },
    actions: {
        async initializeAssetStore(deepState = null) {
            // skip this if we've already loaded
            if (this.loadStatus !== 'not started') return;
            try {
                // Fetch Assets & Gauges
                const [assetResponse, gaugeResponse] = await Promise.all([getAssets(), getConfiguredGaugesAsGeoJSON()]);
                this.handleAssetResponse(deepState, assetResponse);
                this.handleGaugeResponse(gaugeResponse);
                this.loadStatus = 'success';
                return;
            } catch (e) {
                console.error(e);
                this.loadStatus = 'failed';
            }
        },
        clear() {
            this.$reset();
        },
        setActiveGroup(id, deepState = null) {
            this.drawerOpen = this.activeGroupId !== id || !this.drawerOpen || this.chartMode === 'single';
            this.activeGroupId = id;
            if (!this.activeGroup.assetIds.includes(this.activeAssetId)) {
                this.activeAssetId = this.activeGroup.assetIds[0];
            }
            deepState?.setState('group', this.activeGroupId);
            deepState?.setState('asset', this.activeAssetId);
        },
        setActiveAsset(id, deepState = null) {
            if (!this.assetIdList.includes(id)) return;
            this.drawerOpen = this.activeAssetId !== id || !this.drawerOpen || this.chartMode === 'multiple';
            this.activeAssetId = id;
            if (this.activeGroupId && !this.activeAsset?.groupIds.includes(this.activeGroupId)) {
                // set the active group ID to the first group for our selected asset - it would be weird to have a group
                // open that doesn't contain our asset.
                this.activeGroupId = this.activeAsset.groupIds[0];
            }
            deepState?.setState('asset', this.activeAssetId);
        },
        // Recalculate all groups alert levels based on asset risk levels
        resetGroupAlert() {
            this.allGroups.forEach(group => {
                let isAlert = false;
                group.assetIds.forEach(id => {
                    const myAsset = this.assetMap[id];
                    if (myAsset) {
                        if ((myAsset.hasForecasts && myAsset.forecastData.isAlerted) || (myAsset.hasGauge && myAsset.gaugeData.isAlerted)) isAlert = true;
                    }
                });
                group.isAlerted = isAlert;
            });
        },
        handleAssetResponse(deepState = null, assetsResponse) {
            this.assetMap = assetsResponse.baseAssets;
            this.groupMap = assetsResponse.baseGroups;

            this.assetIdList = Object.keys(assetsResponse.baseAssets).map(Number);
            this.groupIdList = Object.keys(assetsResponse.baseGroups).map(Number);

            // Get active location
            if (assetsResponse.activeAssetId !== -1) {
                this.activeAssetId = assetsResponse.activeAssetId;
                this.activeGroupId = assetsResponse.activeGroupId;
            } else if (this.groupIdList.length > 0) {
                this.activeGroupId = this.groupIdList[0];
                if (this.assetIdList.length > 0) this.activeAssetId = this.activeGroup.assetIds[0];
            }

            if (deepState?.getState('asset')) {
                const assetId = +deepState.getState('asset');
                if (this.assetIdList.includes(assetId)) {
                    this.setActiveAsset(assetId);
                } else {
                    deepState.removeState('asset');
                }
            }
            if (deepState?.getState('group')) {
                const groupId = +deepState.getState('group');
                if (this.groupIdList.includes(groupId)) {
                    this.setActiveGroup(groupId);
                } else {
                    deepState.removeState('group');
                }
            }
        },
        handleGaugeResponse(gaugeResponse) {
            this.gaugesGeoJSON = gaugeResponse;
        },
    },
});
